import { shell } from 'punica';

import { sidebarDock } from '../sidebarDock/index.js';
import { Helpers } from '../view/helpers.js';

import activityBarCss from './activityBar.css';

/**
 * DOM id of the single button that stands for a group of activity items.
 * A group is ONE node whatever its member count, so the persistent order slot
 * and the highlight have a stable thing to point at while members come and go.
 */
const groupNodeId = (groupId: string): string => `activity-group-${groupId}`;

class ActivityBarManager implements shell.ActivityBar.IActivityBarManager {
  #primaryItems: Map<string, shell.ActivityBar.IItem> = new Map();
  #secondaryItems: Map<string, shell.ActivityBar.IItem> = new Map();
  #activeId: string | null = null;
  // Id of the activity item whose content-tab tool is currently foreground
  // (Settings / Capability Catalog, etc.). Distinct from #activeId, which
  // tracks the active primary-sidebar view. The effective highlight is
  // `#contentToolActiveId ?? #activeId` — a focused content tool wins.
  #contentToolActiveId: string | null = null;
  #content: HTMLElement;
  #primaryContent: HTMLElement;
  #secondaryContent: HTMLElement;

  // Persistent, per-id insertion order. Assigned the first time an id is seen
  // (which equals extension load order) and NEVER cleared on removeItem, so an
  // item retracted by unload() and re-added by a reload/update drops back into
  // its original slot instead of jumping to the end of its group.
  #order: Map<string, number> = new Map();
  #nextOrder = 0;

  // Group icon/tooltip, keyed by group id. FIRST member to declare them wins,
  // so the group's look does not flip when a later extension loads. Members
  // themselves are not tracked here — they are the items whose `group.id`
  // matches, read out of #primaryItems/#secondaryItems on demand, which keeps
  // one source of truth for what is registered.
  #groupMeta: Map<string, { title?: string; icon?: string }> = new Map();
  // One reusable <punica-menu> per group, refilled on every open.
  #groupMenus: Map<string, HTMLElement> = new Map();

  /**
   * Insert `button` into `container` at the position dictated by its persistent
   * order index, replacing any existing node with the same id in place. New ids
   * get the next order value and land at the end of the group.
   */
  #insertOrdered(container: HTMLElement, button: HTMLElement, id: string): void {
    if (!this.#order.has(id)) {
      this.#order.set(id, this.#nextOrder++);
    }
    const order = this.#order.get(id) as number;

    // Drop any existing node for this id so a re-register never duplicates.
    const existing = container.querySelector<HTMLElement>('#' + id);
    if (existing) {
      container.removeChild(existing);
    }

    // Insert before the first sibling whose order is greater than ours.
    let ref: Element | null = null;
    for (const child of Array.from(container.children)) {
      const childOrder = this.#order.get(child.id);
      if (childOrder !== undefined && childOrder > order) {
        ref = child;
        break;
      }
    }
    container.insertBefore(button, ref);
  }

  /**
   *
   */
  #initiliazeContent() {
    const layoutManager = punica.shell.Layout.manager;
    const activityBarSlot =
      layoutManager.getSlot('activityBar') ??
      // Fallback: if the profile does not define a dedicated activity bar slot,
      // treat the primary sidebar as the host for activity bar content.
      layoutManager.getSlot('primarySidebar');
    const activityBarContent = activityBarSlot?.content;

    // Plain divs, not punica-row/punica-col: the icon-overflow fix needs a
    // reliable flex height chain, and a custom element's shadow-DOM slot doesn't
    // let us set min-height:0 / overflow on the box that actually lays out the
    // buttons — so the column would grow past the bar and clip the bottom group
    // instead of scrolling. Plain divs give us that box directly.
    this.#content = document.createElement('div');
    this.#primaryContent = document.createElement('div');
    this.#secondaryContent = document.createElement('div');

    this.#content.setAttribute(
      'style',
      'display: flex; flex-direction: column; height: 100%;'
    );

    // Primary group takes the remaining height and scrolls internally when more
    // extensions register than fit; the secondary group (Settings, Marketplace)
    // stays pinned at the bottom and always visible.
    this.#primaryContent.classList.add('activityBar-primary-scroll');
    this.#primaryContent.setAttribute(
      'style',
      'display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden;'
    );
    this.#secondaryContent.setAttribute(
      'style',
      'display: flex; flex-direction: column; flex: 0 0 auto;'
    );

    // Hide the scrollbar track — the narrow bar has no room for it; wheel /
    // trackpad scrolling still works. Injected once.
    if (!document.getElementById('activityBar-scroll-style')) {
      const style = document.createElement('style');
      style.id = 'activityBar-scroll-style';
      style.textContent = activityBarCss;
      document.head.appendChild(style);
    }

    this.#content.appendChild(this.#primaryContent);
    this.#content.appendChild(this.#secondaryContent);

    activityBarContent?.appendChild(this.#content);
  }

  constructor() {
    // The dock controller owns the sidebar chrome (docked column vs. overlay
    // drawer, the drawer's scrim, the resize handle). It only needs to know how
    // to close the sidebar when the scrim is pressed.
    sidebarDock.setDismissHandler(() => this.#dismissActiveSidebar());
  }

  /**
   * Close whatever primary-sidebar view is open, through the single visibility
   * authority (`Views.manager.hide`) — the same path the activity-bar toggle-off
   * uses, which de-highlights the icon and tears down the contentSidebar helper.
   * Falls back to `#setActive(null)` for a placement-less legacy view.
   */
  #dismissActiveSidebar(): void {
    const views = (globalThis as any).punica?.shell?.Views?.manager;
    const visible: string | undefined = views?.getVisibleView?.(
      'primarySidebar'
    );
    if (visible) {
      views.hide(visible, { kind: 'user-gesture', id: 'outside-click' });
    } else {
      this.#setActive(null);
    }
  }

  /**
   * Sync the active ActivityBar item with a given primarySidebar view id.
   * This is used by the core/layout to keep the activity bar selection in
   * sync even when views are shown directly (e.g. on startup).
   */
  setActiveByViewId(viewId: string | null): void {
    if (!viewId) {
      this.#setActive(null);
      return;
    }

    for (const item of this.#primaryItems.values()) {
      if ((item as any).viewId === viewId) {
        this.#setActive(item.id);
        return;
      }
    }

    for (const item of this.#secondaryItems.values()) {
      if ((item as any).viewId === viewId) {
        this.#setActive(item.id);
        return;
      }
    }
  }

  /**
   * Reveal a registered view through the `shell.revealView` capability —
   * the substrate's single visibility mutator. Falls back to a direct
   * `Views.manager.reveal` call when the gateway is unavailable (early
   * boot, headless tests) so a user click never dead-ends.
   */
  #revealViaCapability(viewId: string, itemId: string): void {
    const g = globalThis as any;
    const revealDirect = () => {
      const viewsManager = g.punica?.shell?.Views?.manager;
      return viewsManager?.reveal?.(viewId, {
        kind: 'user-gesture',
        id: `activity-bar:${itemId}`
      });
    };

    const gateway = g.punica?.runtime?.capabilities?.gateway;
    if (!gateway?.call) {
      void Promise.resolve(revealDirect()).catch((err: unknown) => {
        console.error('[ActivityBar] Failed to reveal view', viewId, err);
      });
      return;
    }

    const events = g.punica?.kernel?.Events?.manager;
    const traceId: string =
      events?.createCorrelationId?.() ?? `activity-bar-${Date.now()}`;

    void gateway
      .call(
        'shell.revealView',
        { viewId },
        {
          sessionId: 'default',
          actor: { type: 'user' },
          origin: { kind: 'command', id: `activity-bar:${itemId}` },
          trace: { traceId },
          policy: { mode: 'execute' }
        }
      )
      .catch((err: unknown) => {
        console.warn(
          '[ActivityBar] shell.revealView failed, falling back to direct reveal:',
          err
        );
        return Promise.resolve(revealDirect());
      })
      .catch((err: unknown) => {
        console.error('[ActivityBar] Failed to reveal view', viewId, err);
      });
  }

  /**
   * Set the active primary-sidebar item. Manages the sidebar visibility (shown
   * when an item is active, hidden when cleared) through the dock controller.
   * When a sidebar view takes the foreground it also clears any content-tool
   * highlight — the sidebar now owns the selection.
   */
  #setActive(id: string | null): void {
    this.#activeId = id;

    const layoutManager = punica.shell.Layout.manager;

    if (!id) {
      // Sidebar dismissed. A focused content tool (if any) keeps its highlight
      // via #refreshHighlight's fallback.
      sidebarDock.setVisible(false);
      // Also tear down any transient contentSidebar helper (e.g. the extension
      // detail drawer). It is contextual to the primary sidebar, so toggling
      // the sidebar closed must not leave the helper hanging as a zombie
      // overlay. `showView` already resets it when the sidebar SWITCHES views;
      // this covers the toggle-to-close path, which never calls showView.
      (layoutManager as any).resetContentSidebar?.();
    } else {
      // A sidebar view is now foreground — it owns the highlight; drop any
      // content-tool selection and open the sidebar.
      this.#contentToolActiveId = null;
      sidebarDock.setVisible(true);
    }

    this.#refreshHighlight();
  }

  /**
   * Sync the ActivityBar highlight with the active content tab. When that tab
   * was opened by a command-only activity item (one that declares the
   * `contentViewType` it opens), that item's icon becomes active WITHOUT
   * opening the sidebar drawer. Any other active tab (a code file, etc.)
   * clears the content-tool highlight so the sidebar selection shows through.
   */
  syncContentTool(viewType: string | null): void {
    const match = viewType ? this.#findByContentViewType(viewType) : undefined;
    this.#contentToolActiveId = match?.id ?? null;
    this.#refreshHighlight();
  }

  #findByContentViewType(
    viewType: string
  ): shell.ActivityBar.IItem | undefined {
    for (const item of this.#secondaryItems.values()) {
      if ((item as any).contentViewType === viewType) return item;
    }
    for (const item of this.#primaryItems.values()) {
      if ((item as any).contentViewType === viewType) return item;
    }
    return undefined;
  }

  /**
   * Apply the single active-icon highlight. A focused content tool takes
   * precedence over the active sidebar view.
   */
  #refreshHighlight(): void {
    if (!this.#content) return;

    const activeClass = 'extension-is-active';
    const effectiveId = this.#contentToolActiveId ?? this.#activeId;

    const buttons = this.#content.querySelectorAll('.extension');
    buttons.forEach((btn) => btn.classList.remove(activeClass));

    if (!effectiveId) return;
    // A grouped item has no button of its own — the highlight belongs to the
    // group's single node.
    const node = this.#nodeIdFor(effectiveId);
    const next = this.#content.querySelector<HTMLElement>('#' + node);
    if (next) next.classList.add(activeClass);
  }

  /** The DOM id that carries `itemId`'s button: its own, or its group's. */
  #nodeIdFor(itemId: string): string {
    const groupId = this.#findItem(itemId)?.group?.id;
    return groupId ? groupNodeId(groupId) : itemId;
  }

  #findItem(itemId: string): shell.ActivityBar.IItem | undefined {
    return this.#primaryItems.get(itemId) ?? this.#secondaryItems.get(itemId);
  }

  /** Members of a group, in registration order. */
  #membersOf(groupId: string): shell.ActivityBar.IItem[] {
    const members: shell.ActivityBar.IItem[] = [];
    for (const item of this.#primaryItems.values()) {
      if (item.group?.id === groupId) members.push(item);
    }
    for (const item of this.#secondaryItems.values()) {
      if (item.group?.id === groupId) members.push(item);
    }
    return members.sort(
      (a, b) => (this.#order.get(a.id) ?? 0) - (this.#order.get(b.id) ?? 0)
    );
  }

  /**
   * Run an item's action. Extracted from the button's click handler so a
   * flyout row goes down the exact same path — opening a market from its icon
   * or from the group menu must be indistinguishable.
   */
  #activateItem(item: shell.ActivityBar.IItem): void {
    const layoutManager = punica.shell.Layout.manager;
    const viewsManager = (globalThis as any).punica?.shell?.Views?.manager;
    const hasPlacement =
      !!item.viewId && !!viewsManager?.getPlacement?.(item.viewId);
    // View-bound = drives the primary sidebar (a registered placement or a
    // legacy viewId). Command-only items (no view at all) just run a command.
    const isViewBound = hasPlacement || !!item.viewId;

    const runCommand = () => {
      if (!item.command) return;
      try {
        punica.kernel.Commands.manager.executeCommand(item.command);
      } catch (error) {
        console.error(
          '[ActivityBar] Failed to execute command for item',
          item.id,
          item.command,
          error
        );
      }
    };

    // Command-only items (e.g. Settings — a secondary action that opens a
    // CONTENT tab, with no sidebar view) must NOT touch the active state or
    // the primary sidebar drawer. Otherwise `#setActive` re-opens the drawer
    // and the previously-active extension's stale view re-appears there —
    // "clicking Settings activates a different extension in the sidebar".
    // Just run the command; the current sidebar/active item stays put.
    if (!isViewBound) {
      // Command-only item — opens a full-page content tab (e.g. Settings or
      // the Capability Catalog), never a sidebar view. When it declares the
      // content tab it opens, highlight this tool's icon. NEVER re-render a
      // sidebar view here: a `#setActive(item.id)` would re-open the sidebar
      // showing the previously-active extension's stale view (the earlier
      // bug). ContentTabs re-affirms the highlight via `syncContentTool`
      // once the tab is actually selected.
      const contentViewType = (item as any).contentViewType as
        | string
        | undefined;
      if (contentViewType) {
        this.#contentToolActiveId = item.id;
        // In drawer mode the sidebar is an absolute overlay, so one left open
        // sits on top of the content page and clips its left edge — collapse
        // it and drop the active sidebar id so its icon de-highlights. A
        // docked sidebar is a real column beside the content, so it stays
        // open with its selection intact (VS Code behaviour).
        if (sidebarDock.getMode() === 'drawer') {
          this.#activeId = null;
          sidebarDock.setVisible(false);
        }
        // Tear down any transient contentSidebar helper either way — it is an
        // overlay in both modes, so a full-page content tab taking the
        // foreground would leave it sitting on top as a zombie overlay.
        (punica.shell.Layout.manager as any).resetContentSidebar?.();
        this.#refreshHighlight();
      }
      runCommand();
      return;
    }

    // Toggle behavior: if this item is already active, deactivate and hide sidebar.
    if (this.#activeId === item.id) {
      if (hasPlacement) {
        viewsManager.hide(item.viewId, {
          kind: 'user-gesture',
          id: `activity-bar:${item.id}`
        });
      }
      this.#setActive(null);
      return;
    }

    // Mark this item as active in the ActivityBar UI.
    this.#setActive(item.id);

    // Registered view → the single reveal choke point. The user gesture
    // travels through the shell.revealView capability (gateway pipeline:
    // audit, policy) and the ViewRegistry activates the extension,
    // renders its provider (or runs the legacy fallback command) and
    // shows the view. No duplicated show/execute logic here.
    if (hasPlacement) {
      this.#revealViaCapability(item.viewId as string, item.id);
      return;
    }

    // Legacy path: viewId without a registered placement — host the view,
    // then run the command that renders into it.
    try {
      layoutManager.ensureViewHost('primarySidebar', item.viewId as string);
    } catch (error) {
      console.error(
        '[ActivityBar] Failed to show primary view for item',
        item.id,
        item.viewId,
        error
      );
    }
    runCommand();
  }

  /** The icon button shared by plain items and group nodes. */
  #createButton(
    id: string,
    icon: string,
    title: string,
    onClick: (event: MouseEvent) => void
  ): HTMLElement {
    // The icon arrives as finished markup, which `createButton` detects and
    // uses as the button's body rather than as text.
    const button = Helpers.createButton(
      `<punica-icon size="xlarge">${icon}</punica-icon>`,
      onClick,
      { rounded: true, color: 'primary', variant: 'text', size: 'medium' }
    );

    button.setAttribute('id', id);
    button.setAttribute('style', 'margin-bottom: 4px;');
    button.setAttribute('title', title);
    button.className = 'extension';

    return button;
  }

  /**
   * Draw (or redraw) a group's single button.
   *
   * One member: the button IS that member — its icon, its tooltip, its action,
   * no flyout. So declaring a group costs nothing until a second extension
   * joins, and uninstalling back down to one restores the plain icon.
   * Two or more: the group's own icon, and a click opens the flyout.
   */
  #renderGroup(groupId: string): void {
    const nodeId = groupNodeId(groupId);
    const members = this.#membersOf(groupId);

    if (members.length === 0) {
      this.#primaryContent?.querySelector('#' + nodeId)?.remove();
      this.#secondaryContent?.querySelector('#' + nodeId)?.remove();
      this.#groupMenus.get(groupId)?.remove();
      this.#groupMenus.delete(groupId);
      this.#groupMeta.delete(groupId);
      return;
    }

    // The group lives where its FIRST member asked to be; later members join it
    // there rather than splitting the group across the two alignment groups.
    const isPrimary = members[0].alignment === 'primary';
    const container = isPrimary ? this.#primaryContent : this.#secondaryContent;
    const meta = this.#groupMeta.get(groupId) ?? {};
    const single = members.length === 1 ? members[0] : null;

    const button = this.#createButton(
      nodeId,
      single ? single.icon : (meta.icon ?? members[0].icon),
      single ? single.title : (meta.title ?? groupId),
      () => {
        if (single) {
          this.#activateItem(single);
          return;
        }
        this.#openGroupMenu(groupId, button);
      }
    );

    this.#insertOrdered(container, button, nodeId);

    const effectiveActive = this.#contentToolActiveId ?? this.#activeId;
    if (effectiveActive && members.some((m) => m.id === effectiveActive)) {
      button.classList.add('extension-is-active');
    }
  }

  /**
   * Open the group's flyout next to its icon, using the same `punica-menu`
   * the panel tab strip uses for its overflow list — backdrop, Escape, theme
   * and the flip-when-it-does-not-fit are all in the component. `placement`
   * is `right` because the bar hugs the left edge.
   */
  #openGroupMenu(groupId: string, anchor: HTMLElement): void {
    let menu = this.#groupMenus.get(groupId);
    if (!menu) {
      menu = Helpers.createMenu({
        attrs: { id: `${groupNodeId(groupId)}-menu` }
      });
      menu.addEventListener('close', () => menu?.removeAttribute('open'));
      menu.addEventListener('menu-item-click', (e: any) => {
        const itemId = e?.detail?.data as string | undefined;
        menu?.removeAttribute('open');
        const item = itemId ? this.#findItem(itemId) : undefined;
        if (item) this.#activateItem(item);
      });
      this.#content.appendChild(menu);
      this.#groupMenus.set(groupId, menu);
    }

    menu.innerHTML = '';
    const active = this.#contentToolActiveId ?? this.#activeId;
    for (const member of this.#membersOf(groupId)) {
      menu.appendChild(
        Helpers.createMenuItem({
          attrs: { 'data-item': member.id, 'aria-label': member.title },
          // `punica-menu-item` slots its children into a `display: block`
          // host, so an icon and a label sitting next to each other would
          // stack. One flex wrapper puts them on a line; styled inline because
          // the menu clones itself into document.body, out of reach of any
          // scoped stylesheet.
          innerHTML:
            '<span style="display: flex; align-items: center; gap: 8px;">' +
            `<punica-icon size="small">${member.icon}</punica-icon>` +
            `<span>${member.title}</span>` +
            (member.id === active
              ? '<i class="fa-solid fa-check" style="margin-left: auto;" aria-hidden="true"></i>'
              : '') +
            '</span>'
        })
      );
    }

    Helpers.openMenuAt(menu, { anchor, placement: 'right' });
  }

  /**
   *
   * @param alignment
   * @param item
   */
  addItem(item: shell.ActivityBar.IItem): void {
    if (!this.#content) {
      this.#initiliazeContent();
    }

    const isPrimary = item.alignment === 'primary';
    const items = isPrimary ? this.#primaryItems : this.#secondaryItems;

    items.set(item.id, item);

    const groupId = item.group?.id;
    if (groupId) {
      // Claim the member's order slot BEFORE the group's, so the group node
      // lands where its first member would have — the bar does not reshuffle
      // when two icons become one.
      if (!this.#order.has(item.id)) {
        this.#order.set(item.id, this.#nextOrder++);
      }
      if (!this.#groupMeta.has(groupId)) {
        this.#groupMeta.set(groupId, {
          title: item.group?.title,
          icon: item.group?.icon
        });
      }
      this.#renderGroup(groupId);
      return;
    }

    const button = this.#createButton(item.id, item.icon, item.title, () =>
      this.#activateItem(item)
    );

    // Idempotent, position-stable insert. When an extension is updated/reloaded
    // at runtime, unload() removes this item's node and the analysis phase then
    // re-registers it with the same id. #insertOrdered replaces any existing
    // node in place and, on reload, re-inserts at the item's persistent order
    // slot — so the icon is never duplicated and never jumps to the end.
    const targetContent = isPrimary
      ? this.#primaryContent
      : this.#secondaryContent;

    this.#insertOrdered(targetContent, button, item.id);

    // Replacing the DOM node drops any active styling; restore it if this item
    // is the currently-active one (sidebar view or focused content tool) so an
    // update doesn't visually deselect it.
    const effectiveActive = this.#contentToolActiveId ?? this.#activeId;
    if (effectiveActive === item.id) {
      button.classList.add('extension-is-active');
    }
  }

  /**
   *
   * @param itemId
   */
  removeItem(itemId: string): void {
    // A grouped member owns no node of its own: drop it from the registry and
    // let the group redraw — down to one member it becomes that member's plain
    // icon again, down to none the group node goes away.
    const groupId = this.#findItem(itemId)?.group?.id;
    if (groupId) {
      this.#primaryItems.delete(itemId);
      this.#secondaryItems.delete(itemId);
      this.#renderGroup(groupId);
      return;
    }

    if (this.#primaryItems.has(itemId)) {
      const itemElement = this.#primaryContent.querySelector('#' + itemId);

      if (itemElement) {
        this.#primaryContent.removeChild(itemElement);
        this.#primaryItems.delete(itemId);
      }
    } else if (this.#secondaryItems.has(itemId)) {
      const itemElement = this.#secondaryContent.querySelector('#' + itemId);

      if (itemElement) {
        this.#secondaryContent.removeChild(itemElement);
        this.#secondaryItems.delete(itemId);
      }
    }
  }
}

const activityBar: typeof shell.ActivityBar = {
  manager: new ActivityBarManager()
};

export default activityBar;
